import { MultilingualLanguages } from '@asgard/common-dto';
import { useManfredite, useManfrediteUpdate } from 'hooks/api/manfredite';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts/useToasts';
import { ManfrediteCommunication } from 'model';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_LOCALE } from 'services/multilingual.service';
import ServiceEventTracking from 'services/service.event-tracking';
import CookiesService, {
  COOKIES_NEXT_LOCALE
} from 'services/ui/cookies.service';
import { UsePortalSettingsAccount } from './PortalSettingsAccount.types';

export const usePortalSettingsAccount = (): UsePortalSettingsAccount => {
  const { t } = useI18n('portal-settings');
  const { locale, asPath } = useRouter();

  const { manfredite } = useManfredite();
  const { updateManfrediteFromModel } = useManfrediteUpdate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [lang, setLang] = useState<MultilingualLanguages>(
    (locale?.toUpperCase() as MultilingualLanguages) || DEFAULT_LOCALE
  );
  const [communications, setCommunications] =
    useState<ManfrediteCommunication>();
  const { addErrorToast, addSuccessToast } = useToasts();

  // Helper functions.
  // ---------------------------------------------------------------------------
  const fetchData = useCallback(async () => {
    if (!manfredite) return;

    setCommunications({
      newsletter: manfredite?.isNewsletterActive,
      preferredLanguage: manfredite?.preferredLanguage
    });

    setLang(manfredite.preferredLanguage as MultilingualLanguages);
    setSubscribed(manfredite.isNewsletterActive);
  }, [manfredite]);

  const changeLanguage = useCallback(
    (lang: MultilingualLanguages) => {
      CookiesService.add(COOKIES_NEXT_LOCALE, lang);

      // Event tracking with user trait
      ServiceEventTracking.track(
        {
          type: 'Settings - Interface Language Changed',
          properties: {
            title: document?.title,
            fromPreferredLanguage: locale?.toLowerCase(),
            toPreferredLanguage: lang?.toLowerCase()
          }
        },
        {
          preferredLanguage: lang?.toLowerCase()
        }
      );

      const targetPath = `/${lang}${asPath.split('#')[0]}`;

      window.location.href = `${window.location.origin}${targetPath}#languages`;
    },
    [asPath, locale]
  );

  const handleSubmit = useCallback(async () => {
    if (!manfredite) return;

    if (!lang) {
      throw new Error('No new language selected');
    }

    setIsSubmitting(true);

    await updateManfrediteFromModel(
      manfredite,
      {
        isNewsletterActive: subscribed,
        preferredLanguage: lang
      },
      {
        onSuccess: () => {
          const languageHasChanged =
            lang.toLowerCase() !== locale?.toLowerCase();

          if (languageHasChanged) {
            changeLanguage(lang);
          }

          addSuccessToast(t('portal-settings.form.messages.success.save'));
        },

        onError: (ex) => {
          console.error(`Could not save Manfredite account settings: ${ex}`);

          addErrorToast(t('portal-settings.form.messages.error.save'));
        }
      }
    );

    setIsSubmitting(false);
  }, [
    addErrorToast,
    addSuccessToast,
    changeLanguage,
    lang,
    locale,
    manfredite,
    subscribed,
    t,
    updateManfrediteFromModel
  ]);

  // Effects.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!communications) {
      fetchData();
    }
  }, [communications, fetchData]);

  return {
    communications,
    handleSubmit,
    isSubmitting,
    lang,
    setLang,
    setSubscribed,
    subscribed
  };
};
